import {Box} from "@mui/material";
import {type ReactNode, useLayoutEffect, useRef, useState} from "react";

/** A current iPhone's screen in CSS pixels, which is the width the app is designed at. */
export const PHONE_SCREEN_WIDTH = 390;
export const PHONE_SCREEN_HEIGHT = 844;

const BEZEL = 12;
const OUTER_WIDTH = PHONE_SCREEN_WIDTH + BEZEL * 2;
const OUTER_HEIGHT = PHONE_SCREEN_HEIGHT + BEZEL * 2;

/** Breathing room kept below the phone, so its shadow is not cut by the window edge. */
const SPACE_BELOW = 32;
const MIN_SCALE = 0.55;

/**
 * As large as fits between where the frame starts and the bottom of the window, never larger than
 * a real phone. Measured from the frame's own position rather than a guessed header height, so
 * whatever the page puts above it, or beside it, the space left is what it gets.
 */
const fitScale = (top: number) => {
    if (typeof window === "undefined") return 1;
    const available = window.innerHeight - top - SPACE_BELOW;
    return Math.min(1, Math.max(MIN_SCALE, available / OUTER_HEIGHT));
};

/**
 * A generic phone around whatever is shown inside it: a dark bezel, rounded corners, a soft shadow.
 *
 * **Scaled, never resized.** The screen inside is always 390 by 844, so the app lays itself out
 * exactly as it would on a phone, and the whole frame is scaled down to fit a shorter window. Made
 * narrower instead, the app would reflow and show staff a layout no guest ever sees.
 *
 * Deliberately no notch or island: the app on the web does not reserve that space, and a cut-out
 * would sit over its header.
 */
export default function PhoneFrame({children}: {children: ReactNode}) {
    const ref = useRef<HTMLDivElement>(null);
    const [scale, setScale] = useState(1);

    // Before paint, so the phone never shows at full size and then shrinks.
    useLayoutEffect(() => {
        const measure = () => {
            const top = ref.current?.getBoundingClientRect().top ?? 0;
            setScale(fitScale(top + window.scrollY));
        };
        measure();
        window.addEventListener("resize", measure);
        return () => window.removeEventListener("resize", measure);
    }, []);

    return (
        // The outer box takes the scaled size, so the page around it lays out as if the frame
        // really were that small, instead of leaving the unscaled height as empty space.
        <Box ref={ref} sx={{width: OUTER_WIDTH * scale, height: OUTER_HEIGHT * scale, flexShrink: 0}}>
            <Box
                sx={{
                    width: OUTER_WIDTH,
                    height: OUTER_HEIGHT,
                    transform: `scale(${scale})`,
                    transformOrigin: "top left",
                    boxSizing: "border-box",
                    border: `${BEZEL}px solid #111`,
                    borderRadius: "56px",
                    boxShadow: "0 24px 48px rgb(17 17 17 / 18%), 0 4px 12px rgb(17 17 17 / 10%)",
                    overflow: "hidden",
                    bgcolor: "#fff",
                }}
            >
                {children}
            </Box>
        </Box>
    );
}
